import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import { useAssetContext } from '@/context/AssetContext';
import { fetchWithRetry } from '@/utils/fetchWithRetry';
import { useSections } from '@/hooks/useSections';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { JourneySelector } from '@/components/home/JourneySelector';
import SEO from '@/components/SEO';
import CountryFlag from '@/components/CountryFlag';
import { 
  GraduationCap, 
  Globe, 
  Users, 
  Trophy,
  ArrowRight,
  Sparkles,
  Target,
  FileText,
  Home,
  Plane,
  BookOpen,
  Star,
  ChevronRight,
  Play,
  CheckCircle,
  MessageCircle,
  Phone,
  Calendar,
  Clock,
  Award,
  BarChart3,
  Building
} from 'lucide-react';

const API_URL = window.location.origin;

// Icon mapping for dynamic icons from CMS
const iconMap = {
  Users, Globe, Trophy, GraduationCap, Award, Sparkles, Star, Plane, Building, BarChart3, Target, BookOpen
};

// Animated counter hook
const useCounter = (end, duration = 2000, start = 0) => {
  const [count, setCount] = useState(start);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    let startTime;
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      setCount(Math.floor(progress * (end - start) + start));
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  }, [isVisible, end, duration, start]);

  return { count, ref };
};

// StatCard component to properly use hooks
const StatCard = ({ stat, isDark }) => {
  const numericValue = parseInt(stat.value) || 0;
  const { count, ref } = useCounter(numericValue);
  const IconComponent = iconMap[stat.icon] || Star;
  
  return (
    <div 
      ref={ref}
      className={`text-center p-8 rounded-3xl ${isDark ? 'bg-white/5' : 'bg-ux-slate-light'} hover:scale-105 transition-transform`}
    >
      <IconComponent className={`w-8 h-8 mx-auto mb-4 ${isDark ? 'text-ux-cyan' : 'text-ux-blue'}`} />
      <p className={`font-display text-4xl md:text-5xl font-bold ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
        {count}{stat.suffix}
      </p>
      <p className={`mt-2 ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>{stat.label}</p>
    </div>
  );
};

// Default stats data (fallback if CMS is unavailable)
const defaultStats = [
  { id: 'students', icon: 'Users', value: '30000', suffix: '+', label: 'Students Placed' },
  { id: 'countries', icon: 'Globe', value: '15', suffix: '', label: 'Countries' },
  { id: 'visa', icon: 'Trophy', value: '98', suffix: '%', label: 'Visa Success' },
  { id: 'experience', icon: 'GraduationCap', value: '15', suffix: '+', label: 'Yrs Avg Sector Exp' },
];

// Default testimonials (fallback)
const defaultTestimonials = [
  {
    id: '1',
    name: 'Priya Sharma',
    university: 'MIT, USA',
    imageKey: 'homepage_why_choose',
    quote: "UniXperts made my dream of studying at MIT a reality. Their guidance was invaluable!",
    program: 'MS Computer Science'
  },
  {
    id: '2',
    name: 'Rahul Verma',
    university: 'Oxford, UK',
    imageKey: 'homepage_services',
    quote: "From test prep to visa, they were with me every step. Couldn't have done it without them.",
    program: 'MBA'
  },
  {
    id: '3',
    name: 'Ananya Patel',
    university: 'University of Toronto',
    imageKey: 'homepage_cta',
    quote: "The AI university finder matched me perfectly. I'm thriving in Canada now!",
    program: 'BBA Finance'
  },
];

// Default university marquee
const defaultUniversityLogos = [
  'MIT', 'Stanford', 'Harvard', 'Oxford', 'Cambridge', 'Toronto', 'Melbourne', 'ETH Zurich', 'Imperial', 'UCL'
];

// Destinations data - images will be dynamically loaded from AssetContext
const destinationsData = [
  { country: 'Australia', flag: '🇦🇺', universities: 250, slug: 'australia' },
  { country: 'Canada', flag: '🇨🇦', universities: 300, slug: 'canada' },
  { country: 'Dubai', flag: '🇦🇪', universities: 80, slug: 'dubai' },
  { country: 'Germany', flag: '🇩🇪', universities: 200, slug: 'germany' },
  { country: 'New Zealand', flag: '🇳🇿', universities: 50, slug: 'new-zealand' },
  { country: 'UK', flag: '🇬🇧', universities: 400, slug: 'uk' },
  { country: 'USA', flag: '🇺🇸', universities: 500, slug: 'usa' },
];

export const HomePageRedesigned = () => {
  const { isDark } = useTheme();
  const { getDestinationHero, getDestinationCard, getAsset, getHomepageSection } = useAssetContext();
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [showVideoModal, setShowVideoModal] = useState(false);
  
  // CMS Data States
  const [stats, setStats] = useState(defaultStats);
  const [testimonialsData, setTestimonialsData] = useState(defaultTestimonials);
  const [universityLogos, setUniversityLogos] = useState(defaultUniversityLogos);
  const [heroContent, setHeroContent] = useState({
    badge_text: 'Trusted by 30,000+ Students Worldwide',
    title: 'DREAM BIG.',
    title_highlight: 'GO FAR.',
    subtitle: "Your journey to a world-class education starts here. We've helped thousands discover their dream universities and make it a reality.",
    cta_primary_text: 'Find My Dream University'
  });
  const [socialProof, setSocialProof] = useState({
    rating: 4.9,
    total_reviews: "1,000+",
    review_text: "reviews",
    avatar_images: [],
    is_active: true
  });
  const [videoSettings, setVideoSettings] = useState({
    is_enabled: false,  // Disabled for now - can be re-enabled via CMS
    video_type: 'youtube',
    video_url: null,
    video_id: null,
    thumbnail_url: 'https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=800',
    title: 'Discover Your Study Abroad Journey',
    subtitle: 'Watch how UniXperts helps students achieve their dreams'
  });
  const [destinationsCarousel, setDestinationsCarousel] = useState(destinationsData);
  const [heroStatCards, setHeroStatCards] = useState(null);
  const [sectionHeaders, setSectionHeaders] = useState({});
  const [finalCta, setFinalCta] = useState({});
  const [ctaLabels, setCtaLabels] = useState({});

  // Section-based CMS (Phase B Step 4). Prefers section records when present,
  // falls back to legacy page-content / hardcoded values otherwise.
  const { getSection: getSec } = useSections('homepage');
  const heroSec = getSec('hero')?.content;
  const finalCtaSec = getSec('final_cta')?.content;
  // Batch 2 section overlays
  const heroVisualSec = getSec('hero_visual')?.content;
  const videoPromoSec = getSec('video_promo')?.content;
  const marqueeSec = getSec('university_marquee')?.content;
  const testimonialsSec = getSec('testimonials')?.content;
  
  // Fetch CMS data on mount
  useEffect(() => {
    const fetchCMSData = async () => {
      // Fetch stats
      const statsRes = await fetchWithRetry(`${API_URL}/api/cms/site-settings/homepage-stats`, {}, { label: 'homepage-stats' });
      if (statsRes && statsRes.ok) {
        const data = await statsRes.json();
        if (data.stats && data.stats.length > 0) setStats(data.stats);
      }

      // Fetch testimonials
      const testimonialsRes = await fetchWithRetry(`${API_URL}/api/cms/site-settings/testimonials`, {}, { label: 'testimonials' });
      if (testimonialsRes && testimonialsRes.ok) {
        const data = await testimonialsRes.json();
        if (data.testimonials && data.testimonials.length > 0) setTestimonialsData(data.testimonials);
      }

      // Fetch university marquee
      const marqueeRes = await fetchWithRetry(`${API_URL}/api/cms/site-settings/university-marquee`, {}, { label: 'university-marquee' });
      if (marqueeRes && marqueeRes.ok) {
        const data = await marqueeRes.json();
        if (data.universities && data.universities.length > 0) setUniversityLogos(data.universities.map(u => u.name));
      }

      // Fetch video settings
      const videoRes = await fetchWithRetry(`${API_URL}/api/cms/site-settings/video`, {}, { label: 'video-settings' });
      if (videoRes && videoRes.ok) {
        const data = await videoRes.json();
        setVideoSettings(data);
      }

      // Fetch social proof settings
      const socialRes = await fetchWithRetry(`${API_URL}/api/cms/site-settings/social-proof`, {}, { label: 'social-proof' });
      if (socialRes && socialRes.ok) {
        const data = await socialRes.json();
        if (data.rating) setSocialProof(data);
      }

      // Fetch hero content from page content
      const heroRes = await fetchWithRetry(`${API_URL}/api/cms/pages/public/homepage`, {}, { label: 'page-homepage' });
      if (heroRes && heroRes.ok) {
        const data = await heroRes.json();
        if (data.content && data.content.hero) {
          setHeroContent(prev => ({ ...prev, ...data.content.hero }));
        }
        if (data.content && data.content.destinations && data.content.destinations.length > 0) {
          setDestinationsCarousel(data.content.destinations);
        }
        if (data.content && Array.isArray(data.content.hero_stat_cards) && data.content.hero_stat_cards.length > 0) {
          setHeroStatCards(data.content.hero_stat_cards);
        }
        if (data.content && data.content.section_headers) {
          setSectionHeaders(data.content.section_headers);
        }
        if (data.content && data.content.final_cta) {
          setFinalCta(data.content.final_cta);
        }
      }

      // Fetch global settings for CTA labels
      const globalRes = await fetchWithRetry(`${API_URL}/api/cms/pages/public/global-settings`, {}, { label: 'global-settings' });
      if (globalRes && globalRes.ok) {
        const data = await globalRes.json();
        if (data.content && data.content.cta_labels) setCtaLabels(data.content.cta_labels);
      }

      // Also try to fetch from destinations site-settings as fallback
      const destRes = await fetchWithRetry(`${API_URL}/api/cms/site-settings/destinations-carousel`, {}, { label: 'destinations-carousel' });
      if (destRes && destRes.ok) {
        const data = await destRes.json();
        if (data.destinations && data.destinations.length > 0) setDestinationsCarousel(data.destinations);
      }
    };
    
    fetchCMSData();
  }, []);

  // Get hero images from CMS
  const heroDesktop = getAsset('hero_home_desktop');
  const heroMobile = getAsset('hero_home_mobile');
  
  // Build destinations with dynamic images (from CMS or defaults)
  const destinations = destinationsCarousel.map(d => ({
    ...d,
    image: d.image || getDestinationCard(d.slug)
  }));
  
  // ── Section-based CMS overlays (Phase B Step 4 Batch 2) ──
  // If a `testimonials` section is published, its items win over legacy site-settings data.
  const testimonialsSource = (Array.isArray(testimonialsSec?.items) && testimonialsSec.items.length > 0)
    ? testimonialsSec.items.map((t, i) => ({
        id: t.id || `sec-t-${i}`,
        name: t.name,
        university: t.university,
        program: t.program,
        quote: t.quote,
        // useSections stores an asset_key — resolve to a URL via AssetContext.
        image_url: t.photo_asset_key ? (getAsset(t.photo_asset_key) || undefined) : t.image_url,
        image_key: t.photo_asset_key || t.image_key,
      }))
    : testimonialsData;

  // Build testimonials with dynamic images
  const testimonials = testimonialsSource.map(t => ({
    ...t,
    image: t.image_url || getAsset(t.image_key || t.imageKey) || `https://i.pravatar.cc/150?u=${t.name}`
  }));

  // University marquee — section wins over legacy site-settings.
  const effectiveUniversityLogos = (Array.isArray(marqueeSec?.universities) && marqueeSec.universities.length > 0)
    ? marqueeSec.universities.map(u => u.name).filter(Boolean)
    : universityLogos;

  // Hero glass stat cards — section wins over legacy page-content.
  const effectiveHeroStatCards = (Array.isArray(heroVisualSec?.hero_stat_cards) && heroVisualSec.hero_stat_cards.length > 0)
    ? heroVisualSec.hero_stat_cards
    : heroStatCards;

  // Video promo — merge section (title / subtitle / poster) on top of legacy videoSettings.
  // Legacy `is_enabled` + `video_id` + `video_type` remain the source of truth for playback.
  const effectiveVideoSettings = videoPromoSec
    ? {
        ...videoSettings,
        title: videoPromoSec.heading?.text || videoSettings.title,
        subtitle: videoPromoSec.eyebrow || videoSettings.subtitle,
        thumbnail_url: (videoPromoSec.poster_asset_key && getAsset(videoPromoSec.poster_asset_key))
          || videoSettings.thumbnail_url,
        _poster_alt: videoPromoSec.poster_alt || videoSettings.title,
      }
    : videoSettings;

  // Auto-rotate testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  return (
    <>
      <SEO 
        title="Study Abroad Consultancy India | USA UK Canada Australia"
        description="UniXperts - India's leading study abroad consultancy. Get AI-powered university matching, expert counseling & visa support for USA, UK, Canada, Australia, Germany. 30,000+ students placed. Free consultation!"
        keywords="study abroad consultants India, overseas education consultants, USA university admission, UK student visa, Canada PR pathway, Australia student visa, IELTS preparation, GRE coaching, study in USA from India, study in UK from India, study in Canada from India, best study abroad consultants, education consultants near me, abroad studies, MS in USA, MBA abroad"
        type="website"
        canonical="https://unixperts.com/"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "UniXperts",
          "alternateName": "UniXperts Education Consultancy",
          "url": "https://unixperts.com",
          "logo": "https://unixperts.com/logo512.png",
          "description": "India's leading study abroad consultancy helping students achieve their dreams of international education",
          "foundingDate": "2015",
          "sameAs": [
            "https://www.facebook.com/unixperts",
            "https://www.instagram.com/unixperts",
            "https://www.linkedin.com/company/unixperts/about/",
            "https://twitter.com/unixperts"
          ],
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "1st Floor, Plot No. 114, Sector 44",
            "addressLocality": "Gurgaon",
            "addressRegion": "Haryana",
            "postalCode": "122003",
            "addressCountry": "IN"
          },
          "contactPoint": {
            "@type": "ContactPoint",
            "telephone": "+91-124-685-4444",
            "contactType": "customer service",
            "areaServed": "IN",
            "availableLanguage": ["English", "Hindi"]
          },
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.9",
            "reviewCount": "2500",
            "bestRating": "5"
          }
        }}
      />
      <div className={`min-h-screen w-full max-w-[100vw] overflow-x-hidden ${isDark ? 'bg-[#1a2830]' : 'bg-white'} transition-colors duration-500`} data-testid="homepage">
      
      {/* ==================== HERO SECTION - Cinematic Lifestyle ==================== */}
      <section className="relative min-h-[85vh] md:min-h-screen flex items-end md:items-center overflow-hidden">
        {/* Mobile Hero Image - Immersive Vertical Poster */}
        <div className="absolute inset-0 md:hidden">
          <img 
            src={heroMobile}
            alt="Diverse graduates celebrating - Your journey starts here"
            className="w-full h-full object-cover object-[center_25%]"
          />
          {/* Mobile gradient - strong bottom fade for text readability */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#233842]/95 via-[#233842]/50 to-transparent" />
        </div>
        
        {/* Desktop Hero Image - Cinematic Split-Screen */}
        <div className="absolute inset-0 hidden md:block">
          <img 
            src={heroDesktop}
            alt="Students on campus lawn - Your journey starts here"
            className="w-full h-full object-cover object-center"
          />
          {/* Desktop gradient - left side fade for text area */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#233842]/90 via-[#233842]/40 to-transparent" />
        </div>

        <div className="container-max relative z-10 pb-6 pt-16 md:pb-20 md:pt-20">
          <div className="grid lg:grid-cols-2 gap-6 md:gap-12 items-end md:items-center">
            {/* Left Content - Bottom aligned on mobile (Immersive Scrim), Left aligned on desktop */}
            <div className="space-y-3 md:space-y-8 text-left">
              {/* Badge */}
              <div className="animate-fade-up inline-flex items-center gap-2 px-3 md:px-4 py-1.5 md:py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20">
                <Sparkles className="w-3 h-3 md:w-4 md:h-4 text-ux-green" />
                <span className="text-xs md:text-sm font-medium text-white">
                  {heroContent.badge_text}
                </span>
              </div>

              {/* Headline - Space Grotesk for Gen Z impact */}
              <h1 className="font-display text-5xl leading-[0.9] md:text-7xl lg:text-8xl font-bold tracking-tighter text-white uppercase">
                <span className="block animate-fade-up animate-delay-100">{heroContent.title}</span>
                <span className="block animate-fade-up animate-delay-200 bg-gradient-to-r from-ux-cyan to-ux-green bg-clip-text text-transparent">
                  {heroContent.title_highlight}
                </span>
              </h1>

              {/* Subheadline - Tighter on mobile */}
              <p className="animate-fade-up animate-delay-300 text-sm md:text-xl lg:text-2xl font-light leading-relaxed max-w-xl text-white/90">
                {heroContent.subtitle}
              </p>

              {/* CTAs - Electric styling per design guidelines */}
              <div className="animate-fade-up animate-delay-400 flex flex-col sm:flex-row gap-3 md:gap-4 pt-2 w-full md:w-auto">
                <Button 
                  size="lg" 
                  className="w-full sm:w-auto bg-ux-cyan text-ux-charcoal hover:bg-white rounded-full px-8 md:px-12 py-4 md:py-6 text-base md:text-lg font-bold shadow-[0_0_20px_rgba(0,212,255,0.4)] hover:shadow-[0_0_30px_rgba(0,212,255,0.6)] transition-all"
                  data-testid="hero-cta-primary"
                  asChild
                >
                  <Link to="/contact">
                    <Sparkles className="w-4 h-4 md:w-5 md:h-5 mr-2" />
                    {ctaLabels.find_dream_university || heroContent.cta_primary_text || 'Find My Dream University'}
                    <ArrowRight className="w-4 h-4 md:w-5 md:h-5 ml-2" />
                  </Link>
                </Button>
              </div>

              {/* Trust indicators - compact layout for mobile */}
              {socialProof.is_active && (
              <div className="animate-fade-up animate-delay-500 flex items-center gap-2 md:gap-6 pt-1 md:pt-4">
                <div className="flex -space-x-2 md:-space-x-3">
                  {(socialProof.avatar_images?.length > 0 
                    ? socialProof.avatar_images.slice(0, 5)
                    : [1,2,3,4,5].map(i => `https://i.pravatar.cc/100?img=${i+10}`)
                  ).map((src, i) => (
                    <div key={i} className="w-7 h-7 md:w-10 md:h-10 rounded-full border-2 border-white/50 overflow-hidden">
                      <img 
                        src={typeof src === 'string' ? src : `https://i.pravatar.cc/100?img=${src+10}`}
                        alt="" 
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
                <div className="flex flex-col">
                  <div className="flex items-center gap-0.5">
                    {[1,2,3,4,5].map((i) => (
                      <Star 
                        key={i} 
                        className={`w-3 h-3 md:w-4 md:h-4 ${i <= Math.round(socialProof.rating) ? 'text-ux-amber fill-ux-amber' : 'text-gray-400 fill-gray-400'}`}
                      />
                    ))}
                    <span className="ml-1 md:ml-2 text-white font-semibold text-sm md:text-base">{socialProof.rating}</span>
                  </div>
                  <p className="text-xs text-white/70">
                    {socialProof.total_reviews} {socialProof.review_text}
                  </p>
                </div>
              </div>
              )}
            </div>

            {/* Right Content - Glassmorphic Stat Cards (CMS-driven with fallback) */}
            <div className="hidden lg:grid grid-cols-2 gap-4 h-[380px]">
              {(() => {
                const defaultCards = [
                  { icon: 'GraduationCap', value: '500+ Partner Universities', label: '', sublabel: 'Across 15 countries, waiting for you', featured: true },
                  { icon: 'Trophy', value: '98%', label: 'Visa Success Rate' },
                  { icon: 'Clock', value: '15+', label: 'Yrs Avg Sector Exp' }
                ];
                const cards = (effectiveHeroStatCards && effectiveHeroStatCards.length > 0) ? effectiveHeroStatCards : defaultCards;
                const HeroIcon = ({ name, className }) => {
                  const Cmp = iconMap[name] || { GraduationCap, Trophy, Clock }[name] || GraduationCap;
                  return <Cmp className={className} />;
                };
                return cards.map((card, i) => {
                  const isFeatured = i === 0;
                  if (isFeatured) {
                    return (
                      <div key={i} className="col-span-2 backdrop-blur-md bg-gradient-to-br from-ux-cyan/10 to-ux-blue/10 border border-white/15 rounded-2xl p-6 flex items-center gap-5 hover:from-ux-cyan/15 hover:to-ux-blue/15 transition-all group" data-testid={`hero-stat-card-${i}`}>
                        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-ux-cyan to-ux-blue flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform shadow-lg shadow-ux-cyan/30">
                          <HeroIcon name={card.icon} className="w-8 h-8 text-white" />
                        </div>
                        <div>
                          <p className="font-display text-3xl md:text-4xl font-bold text-white">
                            {card.value}{card.label ? ` ${card.label}` : ''}
                          </p>
                          {card.sublabel && (
                            <p className="text-base text-white/70">{card.sublabel}</p>
                          )}
                        </div>
                      </div>
                    );
                  }
                  const gradients = ['from-ux-green to-ux-mint', 'from-ux-violet to-ux-rose', 'from-ux-amber to-orange-500'];
                  const gradient = gradients[(i - 1) % gradients.length];
                  return (
                    <div key={i} className="backdrop-blur-md bg-white/5 border border-white/15 rounded-2xl p-5 flex items-center gap-4 hover:bg-white/10 hover:border-white/25 transition-all group" data-testid={`hero-stat-card-${i}`}>
                      <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform`}>
                        <HeroIcon name={card.icon} className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <p className="font-display text-3xl font-bold text-white">{card.value}</p>
                        <p className="text-sm text-white/70">{card.label}</p>
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        </div>

        {/* Scroll indicator - hidden on mobile to reduce gap */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce hidden md:flex">
          <div className="w-8 h-12 rounded-full border-2 border-white/30 flex items-start justify-center p-2">
            <div className="w-1.5 h-3 rounded-full bg-white/50 animate-pulse" />
          </div>
        </div>
      </section>

      {/* ==================== UNIVERSITY MARQUEE ==================== */}
      <section className={`py-3 md:py-6 overflow-hidden ${isDark ? 'bg-[#233842]' : 'bg-ux-slate-light'}`}>
        <div className="flex items-center gap-4 md:gap-8 animate-marquee whitespace-nowrap flex-nowrap">
          {[...effectiveUniversityLogos, ...effectiveUniversityLogos].map((uni, i) => (
            <div key={i} className={`flex items-center gap-2 px-4 md:px-6 py-2 md:py-3 rounded-full flex-shrink-0 ${isDark ? 'bg-white/5' : 'bg-white'}`}>
              <GraduationCap className={`w-4 h-4 md:w-5 md:h-5 ${isDark ? 'text-ux-cyan' : 'text-ux-blue'}`} />
              <span className={`text-sm md:text-base font-medium whitespace-nowrap ${isDark ? 'text-white/80' : 'text-ux-charcoal'}`}>{uni}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ==================== STATS SECTION ====================
          Hidden on lg+ (desktop) because the hero-right glass stat cards
          already surface these numbers. Shown only on mobile / tablet where
          the hero cards aren't rendered (see the `hidden lg:grid` on the
          right-column stat cards above). Avoids a duplicate figures panel. */}
      <section className={`py-16 md:py-20 lg:hidden ${isDark ? 'bg-[#1a2830]' : 'bg-white'}`} data-testid="homepage-stats-section">
        <div className="container-max">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, i) => (
              <StatCard key={i} stat={stat} isDark={isDark} />
            ))}
          </div>
        </div>
      </section>

      {/* ==================== JOURNEY SELECTOR (Replacing Bento Grid) ==================== */}
      <JourneySelector />

      {/* ==================== DESTINATIONS SECTION ==================== */}
      <section className={`py-12 md:py-24 ${isDark ? 'bg-[#1a2830]' : 'bg-white'}`}>
        <div className="container-max">
          {/* Section Header */}
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-6 md:mb-12 px-4 md:px-0">
            <div>
              <Badge className={`mb-3 ${isDark ? 'bg-ux-violet/20 text-ux-violet' : 'bg-ux-violet/10 text-ux-violet'}`}>
                {sectionHeaders?.destinations?.badge || 'Study Destinations'}
              </Badge>
              <h2 className={`font-display text-3xl md:text-6xl font-bold tracking-tight ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                {sectionHeaders?.destinations?.title || 'Where to'}
                <span className="gradient-text-electric"> {sectionHeaders?.destinations?.title_highlight || 'Next?'}</span>
              </h2>
            </div>
            <Link to="/destinations" className="text-ux-cyan font-medium flex items-center gap-1 mt-4 md:mt-0 hover:gap-2 transition-all">
              {sectionHeaders?.destinations?.link_text || 'View all destinations'} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Mobile: Bento Grid Layout */}
          <div className="md:hidden px-4">
            <div className="grid grid-cols-2 gap-3">
              {destinations.map((dest, i) => {
                // Bento layout for 8 items: 
                // 0=hero(2col), 1-2=square, 3=wide(2col), 4-5=square, 6-7=square
                const isHero = i === 0;
                const isWide = i === 3;
                const colSpan = (isHero || isWide) ? 'col-span-2' : 'col-span-1';
                const height = isHero ? 'h-52' : isWide ? 'h-36' : 'h-40';
                
                return (
                  <Link 
                    key={i}
                    to={`/destinations/${dest.slug}`}
                    className={`${colSpan} group relative overflow-hidden rounded-2xl ${height}`}
                    data-testid={`destination-mobile-${dest.slug}`}
                    aria-label={`Study in ${dest.country}`}
                  >
                    {/* Background Image */}
                    <img 
                      src={dest.image} 
                      alt={dest.country}
                      className="absolute inset-0 w-full h-full object-cover group-active:scale-105 transition-transform duration-500"
                    />
                    
                    {/* Gradient Scrim */}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1a2830]/95 via-[#1a2830]/40 to-transparent" />
                    
                    {/* Glass Content Panel */}
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <div className="flex items-center justify-between mb-1">
                        <CountryFlag country={dest.country} emoji={dest.flag} size={isHero ? 'xl' : 'lg'} />
                        <ChevronRight className="w-5 h-5 text-white/60 group-active:translate-x-1 transition-transform" />
                      </div>
                      <h3 className={`font-serif font-bold text-white mb-2 ${isHero ? 'text-2xl' : 'text-lg'}`} style={{fontFamily: "'Playfair Display', serif"}}>
                        {dest.country}
                      </h3>
                      <div className="inline-flex items-center gap-1.5 bg-white/10 backdrop-blur-sm rounded-full px-3 py-1 border border-white/10">
                        <GraduationCap className="w-3 h-3 text-ux-cyan" />
                        <span className="text-white/90 text-xs font-medium tracking-wide">
                          {dest.universities}+ Unis
                        </span>
                      </div>
                    </div>
                    
                    {/* Focus Ring */}
                    <div className="absolute inset-0 rounded-2xl ring-2 ring-transparent focus-visible:ring-ux-cyan transition-all" />
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Desktop: Horizontal Scroll */}
          <div className="hidden md:flex gap-6 overflow-x-auto pb-6 snap-x snap-mandatory hide-scrollbar">
            {destinations.map((dest, i) => (
              <Link 
                key={i}
                to={`/destinations/${dest.slug}`}
                className="flex-shrink-0 w-72 snap-start group"
                data-testid={`destination-${dest.slug}`}
              >
                <div className="relative h-96 rounded-3xl overflow-hidden">
                  <img 
                    src={dest.image} 
                    alt={dest.country}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                  
                  {/* Content */}
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <div className="flex items-center gap-2 mb-2">
                      <CountryFlag country={dest.country} emoji={dest.flag} size="lg" />
                      <h3 className="font-display text-2xl font-bold text-white">{dest.country}</h3>
                    </div>
                    <p className="text-white/80">{dest.universities}+ Universities</p>
                    
                    <div className="mt-4 flex items-center text-ux-cyan font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                      Explore <ChevronRight className="w-4 h-4 ml-1" />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== TESTIMONIALS ==================== */}
      <section className={`py-24 ${isDark ? 'bg-[#233842]' : 'bg-ux-slate-light'}`}>
        <div className="container-max">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Content */}
            <div>
              <Badge className={`mb-4 ${isDark ? 'bg-ux-amber/20 text-ux-amber' : 'bg-ux-amber/10 text-ux-amber'}`}>
                {sectionHeaders?.testimonials?.badge || 'Success Stories'}
              </Badge>
              <h2 className={`font-display text-4xl md:text-5xl font-bold tracking-tight mb-4 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                {sectionHeaders?.testimonials?.title || 'Students Who'}
                <span className="gradient-text-sunset"> {sectionHeaders?.testimonials?.title_highlight || 'Made It'}</span>
              </h2>
              <Link
                to="/success-stories"
                data-testid="home-see-all-stories-link"
                className={`inline-flex items-center gap-1.5 text-sm font-medium mb-8 group ${isDark ? 'text-ux-cyan hover:text-white' : 'text-ux-blue hover:text-ux-charcoal'}`}
              >
                See all success stories
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>

              {/* Testimonial Card */}
              <div className="glass-panel rounded-3xl p-8">
                <div className="flex items-center gap-4 mb-6">
                  <img 
                    src={testimonials[activeTestimonial].image}
                    alt={testimonials[activeTestimonial].name}
                    className="w-16 h-16 rounded-full object-cover border-2 border-ux-cyan"
                  />
                  <div>
                    <h4 className={`font-display text-xl font-bold ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                      {testimonials[activeTestimonial].name}
                    </h4>
                    <p className="text-ux-cyan">{testimonials[activeTestimonial].university}</p>
                    <p className={`text-sm ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>
                      {testimonials[activeTestimonial].program}
                    </p>
                  </div>
                </div>
                <blockquote className={`text-lg italic ${isDark ? 'text-white/80' : 'text-ux-charcoal/80'}`}>
                  "{testimonials[activeTestimonial].quote}"
                </blockquote>
                
                {/* Dots */}
                <div className="flex gap-2 mt-6">
                  {testimonials.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveTestimonial(i)}
                      className={`w-2 h-2 rounded-full transition-all ${
                        i === activeTestimonial 
                          ? 'w-6 bg-ux-cyan' 
                          : isDark ? 'bg-white/30' : 'bg-ux-charcoal/30'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Right - Video Section (CMS Managed) */}
            {effectiveVideoSettings.is_enabled && (
              <div className="relative h-96 rounded-3xl overflow-hidden">
                {/* Thumbnail */}
                <img 
                  src={effectiveVideoSettings.thumbnail_url || 'https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=800'}
                  alt={effectiveVideoSettings._poster_alt || effectiveVideoSettings.title || 'Students'}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center">
                  {/* Title overlay (optional) */}
                  {effectiveVideoSettings.title && (
                    <p className="text-white/80 text-sm mb-4 font-medium tracking-wide uppercase">
                      {effectiveVideoSettings.subtitle}
                    </p>
                  )}
                  {/* Play button */}
                  <button 
                    onClick={() => setShowVideoModal(true)}
                    className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center hover:scale-110 hover:bg-white/30 transition-all group"
                    disabled={!effectiveVideoSettings.video_id}
                    data-testid="homepage-video-play-btn"
                  >
                    <Play className="w-8 h-8 text-white fill-white ml-1 group-hover:scale-110 transition-transform" />
                  </button>
                  {!effectiveVideoSettings.video_id && (
                    <p className="text-white/60 text-sm mt-4">Video coming soon</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Video Modal */}
      {showVideoModal && videoSettings.video_id && (
        <div 
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setShowVideoModal(false)}
        >
          <div 
            className="relative w-full max-w-4xl aspect-video"
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              onClick={() => setShowVideoModal(false)}
              className="absolute -top-12 right-0 text-white hover:text-ux-cyan transition-colors"
            >
              <span className="text-lg">Close</span>
            </button>
            {videoSettings.video_type === 'youtube' ? (
              <iframe
                className="w-full h-full rounded-xl"
                src={`https://www.youtube.com/embed/${videoSettings.video_id}?autoplay=1`}
                title={videoSettings.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : videoSettings.video_type === 'vimeo' ? (
              <iframe
                className="w-full h-full rounded-xl"
                src={`https://player.vimeo.com/video/${videoSettings.video_id}?autoplay=1`}
                title={videoSettings.title}
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <video 
                className="w-full h-full rounded-xl" 
                src={videoSettings.video_url} 
                controls 
                autoPlay
              />
            )}
          </div>
        </div>
      )}

      {/* ==================== CTA SECTION - Cinematic Full-bleed ==================== */}
      <section className="relative py-32 overflow-hidden">
        {/* Full-bleed background */}
        <div className="absolute inset-0">
          <img 
            src={getHomepageSection('students_collab') || 'https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=800'}
            alt="Graduates celebrating"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-ux-violet/90 via-ux-blue/80 to-ux-cyan/70" />
        </div>
        
        <div className="container-max relative z-10 text-center">
          <h2 className="font-display text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
            {finalCtaSec?.heading?.text || finalCta.title || (<>Ready to Start Your<br />Global Education Journey?</>)}
          </h2>
          <p className="text-xl md:text-2xl text-white/80 max-w-2xl mx-auto mb-10 font-light">
            {finalCtaSec?.sub_heading?.text || finalCta.subtitle || 'Get your personalized study abroad strategy. Our experts are ready to guide you every step of the way.'}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              className="bg-white text-ux-blue rounded-full px-10 py-6 text-lg font-bold hover:bg-ux-green hover:text-ux-charcoal hover:scale-105 transition-all"
              asChild
            >
              <Link to={
                finalCtaSec?.cta_primary?.href
                || (finalCta.cta_primary_link && !finalCta.cta_primary_link.startsWith('/uac-desk') ? finalCta.cta_primary_link : '/contact')
              }>
                {finalCtaSec?.cta_primary?.label || finalCta.cta_primary_text || ctaLabels.book_consultation || 'Book Free Consultation'}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </Button>
            <Button 
              size="lg"
              variant="hero-outline"
              className="rounded-full px-10 py-6 text-lg font-medium"
              asChild
            >
              <Link to={finalCtaSec?.cta_secondary?.href || finalCta.cta_secondary_link || '/contact'}>
                <MessageCircle className="w-5 h-5 mr-2" />
                {finalCtaSec?.cta_secondary?.label || finalCta.cta_secondary_text || ctaLabels.talk_to_expert || 'Talk to an Expert'}
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* ==================== MOBILE STICKY CTA ==================== */}
      <div className="fixed bottom-0 left-0 right-0 z-40 p-4 md:hidden">
        <div className={`flex items-center justify-around gap-2 p-3 rounded-2xl ${isDark ? 'bg-[#233842]/95' : 'bg-white/95'} backdrop-blur-lg border ${isDark ? 'border-white/10' : 'border-ux-charcoal/10'} shadow-lg`}>
          <a href="tel:+919636957799" className="flex flex-col items-center gap-1 flex-1 py-2">
            <Phone className={`w-5 h-5 ${isDark ? 'text-ux-cyan' : 'text-ux-blue'}`} />
            <span className={`text-xs ${isDark ? 'text-white/80' : 'text-ux-charcoal'}`}>Call</span>
          </a>
          <Link to="/contact" className="flex-1 py-2 px-4 bg-gradient-to-r from-ux-cyan to-ux-blue rounded-xl text-center">
            <span className="text-white text-sm font-bold">Book Free Call</span>
          </Link>
        </div>
      </div>
    </div>
    </>
  );
};

export default HomePageRedesigned;
